import React, { useState } from "react";
import { ProductTemplateInfo } from "./models";
import { EditSectionTemplate } from "components/MainSectionTemplate/EditSectionTemplate";
import { routes } from "utilities/routing/generatedRoutes";
import ProductTypesDropdown from "components/dropdowns/reactselect/ProductTypesDropdown";
import ProductGroupDropdown from "components/dropdowns/reactselect/ProductGroupDropdown";
import RequiredTextInput from "components/ValidatedFields/RequiredTextInput";
import RequiredNumberInput from "components/ValidatedFields/RequiredNumberInput";
import OptionalTextAreaInput from "components/ValidatedFields/OptionalTextAreaInput";
import OptionalTextInput from "components/ValidatedFields/OptionalTextInput";
import VatRateDropdown from "components/dropdowns/reactselect/VatRateDropdown";
import { PriceInput } from "./PriceInput";
import { usePriceState } from "./usePriceState";

interface ProductTemplateNewSectionProps {
  productGroups: server.dto.ProductGroup[]
  vatRates: server.dto.VatRate[]
}

export const ProductTemplateNewSection = (props: ProductTemplateNewSectionProps) => {
  const [value, setValue] = useState<ProductTemplateInfo>({
    productType: serverReference.productTypes.find(pt => pt.value === "undefined"),
    productGroup: null,
    ledgerNumber: serverReference.constants.productTemplate.defaultLedgerNumber,
    internalName: "",
    title: "",
    description: "",
    unit: "",
  });
  const [costsExVat, costsIncVat, costsIsIncVat, costsVatRate, onCostsVatRateChange, onCostsValueChange] = usePriceState(props.vatRates[0], false, 0);
  const [salesExVat, salesIncVat, salesIsIncVat, salesVatRate, onSalesVatRateChange, onSalesValueChange] = usePriceState(props.vatRates[0], false, 0);

  const onChange = (newData: Partial<ProductTemplateInfo>) => {
    setValue({ ...value, ...newData });
  };

  return <EditSectionTemplate
    submit={() => window.CoreApi.ProductTemplates.Create({
      costsPricePerUnit: costsExVat,
      costsVatRateId: costsVatRate.vatRateId,

      descriptionForTemplate: value.description,
      ledgerNumber: value.ledgerNumber,
      productGroupId: value.productGroup?.productGroupId || null,
      productType: value.productType.value,

      salesPricePerUnit: salesExVat,
      salesVatRateId: salesVatRate.vatRateId,

      title: value.internalName,
      titleForTemplate: value.title,
      unitForTemplate: value.unit,
    })}
    redirectUrl={(itemId) => routes.productTemplate_Detail(itemId).absolute}
    sections={[{
      key: "productTemplate",
      type: "regular",
      title: RESX.ProductTemplate.resxProductTemplate,
      fields: [{
        key: "productType",
        label: RESX.ProductTemplate.ProductType,
        renderEditNew: () => <ProductTypesDropdown
          value={value.productType}
          onChange={(productType) => { onChange({ productType }); }}
        />
      }, {
        key: "productGroup",
        label: RESX.ProductGroup.resxProductGroup,
        hidden: !site.currentEnvironment.hasProductGroups,
        renderEditNew: () => <ProductGroupDropdown
          options={props.productGroups}
          value={value.productGroup}
          onChange={(productGroup) => { onChange({ productGroup }); }}
        />
      }, {
        key: "internalName",
        label: RESX.ProductTemplate.InternalName,
        renderEditNew: () => <RequiredTextInput
          validatorKey="internalName"
          placeholder={RESX.ProductTemplate.ProductCodeForOwnUse}
          maxLength={serverReference.constants.productTemplate.maxTitleLength}
          value={value.internalName}
          onChange={(internalName) => { onChange({ internalName }); }}
        />
      }, {
        key: "ledger",
        label: RESX.ProductGroup.Ledger,
        supportArticleId: serverReference.supportArticleIds.whatIsTheLedgerFor,
        renderEditNew: () => <RequiredNumberInput
          width={90}
          validatorKey="ledger"
          minValue={1}
          maxValue={Math.pow(10, serverReference.constants.productTemplate.maxLedgerNumberLength) - 1}
          value={value.ledgerNumber}
          decimalPoints={0}
          onChange={(ledgerNumber) => { onChange({ ledgerNumber }); }}
        />
      }, {
        key: "title",
        label: RESX.GeneralLabels.Title,
        renderEditNew: () => <RequiredTextInput
          validatorKey="title"
          maxLength={serverReference.constants.productTemplate.maxTitleForTemplateLength}
          value={value.title}
          onChange={(title) => { onChange({ title }); }}
        />
      }, {
        key: "description",
        label: RESX.GeneralLabels.Description,
        renderEditNew: () => <OptionalTextAreaInput
          maxLength={serverReference.constants.productTemplate.maxDescriptionForTemplateLength}
          value={value.description}
          onChange={(description) => { onChange({ description }); }}
        />
      }, {
        key: "unit",
        label: RESX.ProductTemplate.Unit,
        supportArticleId: serverReference.supportArticleIds.unitInProduct,
        renderEditNew: () => <OptionalTextInput
          maxLength={serverReference.constants.productTemplate.maxUnitLength}
          value={value.unit}
          onChange={(unit) => { onChange({ unit }); }}
        />
      }]
    }, {
      type: "regular",
      key: "costs",
      title: RESX.Financial.CostsPerUnit,
      fields: [{
        key: "costsVatRate",
        label: RESX.Financial.TaxRate,
        renderEditNew: () => <VatRateDropdown
          options={props.vatRates}
          value={costsVatRate}
          onChange={(vatRate) => { onCostsVatRateChange(vatRate) }}
        />
      }, {
        key: "price",
        label: RESX.Financial.Price,
        renderEditNew: () => <PriceInput
          valueExVat={costsExVat}
          valueIncVat={costsIncVat}
          isValueIncVat={costsIsIncVat}
          onChange={(isValueIncVat, value) => {
            onCostsValueChange(isValueIncVat, value);
          }}
        />,
      }]
    }, {
      type: "regular",
      key: "sales",
      title: RESX.Financial.SalesPerUnit,
      fields: [{
        key: "salesVatRate",
        label: RESX.Financial.TaxRate,
        renderEditNew: () => <VatRateDropdown
          options={props.vatRates}
          value={salesVatRate}
          onChange={(vatRate) => { onSalesVatRateChange(vatRate) }}
        />
      }, {
        key: "price",
        label: "Price",
        renderEditNew: () => <PriceInput
          valueExVat={salesExVat}
          valueIncVat={salesIncVat}
          isValueIncVat={salesIsIncVat}
          onChange={(isValueIncVat, value) => {
            onSalesValueChange(isValueIncVat, value);
          }}
        />,
      }]
    }]}
  />
}